import { useSuspenseQueries, type QueryClient } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import useInstantPayEligible from '@/enterprise/components/InstantPay/useInstantPayEligible';
import apiClient from '@/lib/apiClient';
import queryClient from '@/lib/queryClient';
import { ExchangeRate } from '@/shared/data/useExchangeRates';
import calculatePercentage from '@/utils/calculatePercentage';
import { getInstantPayEnrollment, InstantPayEnrollment } from './instantPayEnrollment';
import {
  SEAPricingSuggestionRequestBody,
  SEAPricingSuggestionResponse,
  SEAPricingSuggestionSupplierMarket,
} from './usePricingSuggestion';

export type Pricing = 'BENCHMARK' | 'PRICE_DISCOVERY' | 'STATIC';

export interface BenchmarkRateInfo {
  type: 'BenchmarkRateInfo';
  estimatedRate?: number | null;
  rateName: string;
  referenceRate: number;
  totalSpread?: number | null;
}

export interface PreferredRateInfo {
  type: 'PreferredRateInfo';
  rateSourceName: string;
  referenceRate: number;
  spread?: number | null;
  totalRate: number;
}

export type OfferRateUnion = BenchmarkRateInfo | PreferredRateInfo;

type OfferFrequency = 'ONE_TIME' | 'RECURRING' | undefined;

export const OfferOptionType = {
  PREFERRED_TERM: 'preferred-term',
  PRICE_DISCOVERY: 'price-discovery',
  BENCHMARK: 'benchmark',
  EXPRESS_ACCEPT: 'express-accept',
  STATIC: 'static',
  UNKNOWN: 'unknown',
} as const;

export const OfferOptionFrequency = {
  ONE_TIME: 'one-time',
  RECURRING: 'recurring',
  UNKNOWN: 'unknown',
} as const;

export type OfferOptionTypeValue = (typeof OfferOptionType)[keyof typeof OfferOptionType];
export type OfferOptionFrequencyValue = (typeof OfferOptionFrequency)[keyof typeof OfferOptionFrequency];

export interface OfferOption {
  type: OfferOptionTypeValue;
  frequency: OfferOptionFrequencyValue;
}

export interface OfferOptions {
  newOffer: OfferOption[];
  availableOffer: OfferOption[];
}

export interface RenewalInformation {
  enabled: boolean;
  termDays: number;
}

export interface OfferConfig {
  uuid?: string | null;
  divisionId: number;
  exclusionSettings: {
    excludeNewInvoices?: boolean | null;
  };
  isReferenceRateBidding?: boolean | null;
  isDiscountBidding?: boolean | null;
  isEnabled?: boolean;
  marketId: string;
  marketUuid: string;
  maxApr?: number | null;
  maxDiscount?: number | null;
  offerType?: string | null;
  type?: string | null;
  frequency?: OfferFrequency;
  isPreferredRenewalRequested?: boolean | null;
  isManuallyAwarded: boolean;
  // An express-accept offer can have all invoices removed from clear, making isManuallyAwarded false even though the offer is still active
  isActiveExpressAccept: boolean;
  status: SEAOffer['status'] | null;
}

type RateInfo = OfferRateUnion;

type SEAMarketResponse = paths['/supplier-markets']['get']['responses']['200']['content']['application/json'];
type SEAMarketOfferType =
  SEAMarketResponse['data']['supplierMarketGroups'][0]['supplierMarkets'][0]['offer']['offerType'];
export type SEAMarketPricingType = SEAMarketResponse['data']['supplierMarketGroups'][0]['meta']['pricingType'];
type SEAMarketOfferOptions = SEAMarketResponse['data']['supplierMarketGroups'][0]['meta']['offerOptions'];
type SEAMarketParams = paths['/supplier-markets']['get']['parameters']['query'];

type SEAOfferResponse = paths['/offers']['get']['responses']['200']['content']['application/json'];
type SEAOfferParams = paths['/offers']['get']['parameters']['query'];

type UserSupplierResponse = paths['/user/suppliers']['get']['responses']['200']['content']['application/json'];
type UserSupplier = UserSupplierResponse['data']['suppliers'][0];

export type SEAMarketGroup = SEAMarketResponse['data']['supplierMarketGroups'];
export type SEATakerMarket = SEAMarketGroup[number];

type SEAOfferGroup = SEAOfferResponse['data'];
export type SEAOffer = SEAOfferGroup[0];
interface RecurringSchedule {
  // At launch, only "days of week" is supported for recurring schedules
  daysOfWeek?: ('Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday')[];
}

interface FutureSchedule {
  frequency: 'one-time' | 'recurring' | 'unknown';
  rate: number;
  rateType: 'APR' | 'DISC' | 'REF';
}

interface RenewalSchedule {
  renewalIntervalDays: number;
}

export interface OfferSchedule {
  status: 'pending' | 'active' | 'paused' | 'cancelled' | 'expired';
  type: 'future' | 'recurring' | 'renewal';
  schedule: RecurringSchedule | FutureSchedule | RenewalSchedule;
  marketUuid: string;
  supplierDivisionUuid: string;
  makerOrganizationUuid: string;
  timezone: string;
  startDate?: string;
  endDate?: string;
}

export type MarketFeature = 'PREFERRED_ELIGIBLE';

export interface TakerMarketInstantPayStats {
  invoiceCount: number;
  invoiceAmount: number;
  invoiceAmountUsd: number;
  dpeWeightedSum: number;
  dpeWeightedAvg: number;
}

export interface TakerMarket {
  id: string; // not based on c2fo db, just a unique id generated client side for taker market rows
  acceptedDpeWeightedSum: number;
  acceptedEarn: number;
  acceptedInvoiceAmount: number;
  acceptedInvoiceCount: number;
  clearingEarn: number;
  clearingDpeWeightedSum: number;
  clearingInvoiceAmount: number;
  clearingInvoiceCount: number;
  clearingDiscountWeightedAvg: number;
  clearingAprWeightedAvg: number;
  clearingDpeWeightedAvg: number;
  acceptancePercentage: number;
  awardedInvoiceAmount: number;
  awardedInvoiceAmountUsd: number;
  awardedDpeWeightedSum: number;
  awardedInvoiceCount: number;
  configExclusionSettings?: {
    excludeNewInvoices?: boolean;
  };
  configId?: number | null;
  configIsDiscountBidding?: boolean;
  configIsEnabled?: boolean;
  configMaxApr?: number | null;
  configMaxDiscount?: number | null;
  currency: string;
  disableAfterMarketClearsDate?: string | null;
  eligibleDpeWeightedAvg: number;
  eligibleDpeWeightedSum: number;
  eligibleInvoiceAmount: number;
  eligibleInvoiceAmountUsd: number;
  eligibleInvoiceCount: number;
  isDiscountMarket: boolean;
  instantPay?: InstantPayEnrollment | null;
  // instantPay above is the enrollment, not stats
  instantPayStats?: TakerMarketInstantPayStats | null;
  isInstantPayAllowed: boolean;
  isTpf?: boolean;
  lastClearDate?: string | null;
  legacyMarketId: number;
  makerDivisionName: string;
  makerDivisionId: number;
  makerOrganizationName: string;
  makerOrganizationUuid: string;
  makerOrganizationLogo: string | null;
  marketId: string;
  marketIsEnabled: boolean;
  marketIsInactive: boolean;
  marketMaxApr?: number | null;
  marketMaxDiscount?: number | null;
  marketNextClearTime: string | null;
  marketPayDate: string | null;
  marketGoodFundsDate: string | null;
  marketPricingType?: Pricing | null;
  marketFeatures: MarketFeature[];
  marketIsDSF: boolean;
  staticMarketRate?: number | null;
  marketType?: string;
  marketUuid: string;
  notAcceptedDpeWeightedAvg: number;
  notAcceptedDpeWeightedSum: number;
  notAcceptedInvoiceAmount: number;
  notAcceptedInvoiceCount: number;
  offerConfig: OfferConfig;
  offerOptions: OfferOptions;
  rateInfos?: RateInfo[] | null;
  takerDivisionId: number;
  takerDivisionUuid: string;
  takerDivisionName: string;
  takerExcludedInvoiceAmount: number;
  takerExcludedInvoiceCount: number;
  takerMakerId: string;
  userAddedToDivision?: string;
  offerSchedule?: OfferSchedule;
  offerStatsStatus: 'PENDING' | 'COMPLETED' | null;
  lastFetchedAt: number;
  totalInvoiceAmount: number;
  totalInvoiceAmountUsd: number;
  totalInvoiceCount: number;
}

const getSEASupplierMarketRateInfos = (
  pricingSuggestion?: SEAPricingSuggestionSupplierMarket,
  offer?: SEAOffer
): RateInfo[] => {
  const rateInfos = [];

  // For taker markets that already have an offer,
  // only store a single rate info for the offer
  if (offer?.rateInformation) {
    if (offer.rateInformation.type === 'benchmark') {
      return [
        {
          type: 'BenchmarkRateInfo',
          estimatedRate: offer.rateInformation.rate,
          rateName: offer.rateInformation.rateName,
          referenceRate: offer.rateInformation.referenceRate ?? 0,
          totalSpread: offer.rateInformation.spread,
        },
      ];
    } else if (
      offer.rateInformation.type === 'preferred' &&
      offer.rateInformation.rateType === 'REF' &&
      'rateName' in offer.rateInformation
    ) {
      return [
        {
          type: 'PreferredRateInfo',
          rateSourceName: offer.rateInformation.rateName,
          referenceRate: offer.rateInformation.referenceRate ?? 0,
          totalRate: offer.rateInformation.rate,
          spread: offer.rateInformation.spread,
        },
      ];
    } else if (offer.rateInformation.type === 'preferred' && 'rateName' in offer.rateInformation) {
      return [
        {
          type: 'PreferredRateInfo',
          rateSourceName: offer.rateInformation.rateName,
          referenceRate: 0,
          totalRate: offer.rateInformation.rate,
        },
      ];
    }
  }

  // For taker markets without offers, include both benchmark and preferred rate
  // info from the pricing suggestion
  if (pricingSuggestion && pricingSuggestion.rates.length > 0) {
    const benchmarkRateInfo = pricingSuggestion.rates
      .find((rate) => {
        return rate.rateInformationType === 'benchmark';
      })
      ?.rates?.find((rate) => {
        return rate.type === 'benchmark';
      });

    const preferredRateInfo = pricingSuggestion.rates
      .find((rate) => {
        return rate.rateInformationType === 'preferred';
      })
      ?.rates?.find((rate) => {
        return rate.type === 'preferred';
      });

    if (preferredRateInfo && 'rateName' in preferredRateInfo) {
      rateInfos.push({
        type: 'PreferredRateInfo' as const,
        rateSourceName: preferredRateInfo.rateName,
        referenceRate: preferredRateInfo.referenceRate ?? 0,
        totalRate: preferredRateInfo.rate,
        spread: preferredRateInfo.spread,
      });
    }

    if (benchmarkRateInfo) {
      rateInfos.push({
        type: 'BenchmarkRateInfo' as const,
        estimatedRate: benchmarkRateInfo.rate,
        rateName: benchmarkRateInfo.rateName,
        referenceRate: benchmarkRateInfo.referenceRate ?? 0,
        totalSpread: benchmarkRateInfo.spread,
      });
    }
  }

  return rateInfos;
};

const convertSEAPricing = (pricing: SEAMarketPricingType): Pricing | null => {
  switch (pricing) {
    case 'price-discovery':
      return 'PRICE_DISCOVERY';
    case 'static':
      return 'STATIC';
    case 'benchmark':
      return 'BENCHMARK';
    default:
      return null;
  }
};

const convertSEAOfferType = (offerType: SEAMarketOfferType): string | undefined => {
  switch (offerType) {
    case 'preferred-term':
      return 'PREFERRED_TERM';
    case 'express-accept':
      return 'EXPRESS_ACCEPT';
    default:
      return undefined;
  }
};

const convertSEAOfferFrequency = (frequency?: SEAOfferResponse['data'][0]['frequency']): OfferFrequency => {
  switch (frequency) {
    case 'one-time':
      return 'ONE_TIME';
    case 'recurring':
      return 'RECURRING';
    default:
      return undefined;
  }
};

const convertOfferOptionType = (type: SEAMarketOfferOptions['newOffer'][0]['type']): OfferOptionTypeValue => {
  switch (type) {
    case 'preferred-term':
      return OfferOptionType.PREFERRED_TERM;
    case 'price-discovery':
      return OfferOptionType.PRICE_DISCOVERY;
    case 'benchmark':
      return OfferOptionType.BENCHMARK;
    case 'express-accept':
      return OfferOptionType.EXPRESS_ACCEPT;
    case 'static':
      return OfferOptionType.STATIC;
    default:
      return OfferOptionType.UNKNOWN;
  }
};

const convertOfferOptionFrequency = (
  frequency: SEAMarketOfferOptions['newOffer'][0]['frequency']
): OfferOptionFrequencyValue => {
  switch (frequency) {
    case 'one-time':
      return OfferOptionFrequency.ONE_TIME;
    case 'recurring':
      return OfferOptionFrequency.RECURRING;
    default:
      return OfferOptionFrequency.UNKNOWN;
  }
};

const convertSEAOfferOptions = (seaOfferOptions: SEAMarketOfferOptions): OfferOptions => {
  return {
    newOffer:
      seaOfferOptions.newOffer?.map((option) => ({
        type: convertOfferOptionType(option.type),
        frequency: convertOfferOptionFrequency(option.frequency),
      })) ?? [],
    availableOffer:
      seaOfferOptions.availableOffer?.map((option) => ({
        type: convertOfferOptionType(option.type),
        frequency: convertOfferOptionFrequency(option.frequency),
      })) ?? [],
  };
};

export const formatSEASupplierMarkets = (
  supplierMarkets: SEAMarketGroup,
  offers: SEAOfferGroup,
  pricingSuggestions: SEAPricingSuggestionSupplierMarket[],
  usdExchangeRates?: ExchangeRate
): TakerMarket[] => {
  // Grouping defaults to SUPPLIER_MARKET, assume one TM per group
  return supplierMarkets.map((supplierMarket) => {
    const sm = supplierMarket.supplierMarkets[0];

    let exchangeRate = usdExchangeRates?.[sm.market.currency] ?? 1;

    const pricingSuggestion = pricingSuggestions.find((suggestion) => {
      return (
        suggestion.supplierMarkets[0].marketUuid === sm.market.uuid &&
        suggestion.supplierMarkets[0].supplierDivisionUuid === sm.supplier.uuid
      );
    });

    const staticMarketRate = pricingSuggestion?.rates
      .find((rate) => {
        return rate.rateInformationType === 'static';
      })
      ?.rates?.find((rate) => {
        return rate.type === 'static';
      });

    if (exchangeRate === 0) {
      exchangeRate = 1;
    }

    const seaOffer = offers.find((offer) => {
      return (
        offer.supplierMarkets[0].marketUuid === sm.market.uuid &&
        offer.supplierMarkets[0].supplierDivisionUuid === sm.supplier.uuid
      );
    });

    /**
     * Note 1: "Pending" is a derived type of "Active" schedule, meaning it has a start date in the future.
     * Note 2: The platform only supports one "Active" schedule per offer, so we return a single schedule here for ease of use and less looping logic throughout the app.
     */
    const offerSchedule: OfferSchedule | undefined = seaOffer?.schedules?.find(
      (offerSchedule): offerSchedule is OfferSchedule => {
        return (
          (offerSchedule.status === 'active' || offerSchedule.status === 'pending') &&
          (offerSchedule.type === 'recurring' || offerSchedule.type === 'future' || offerSchedule.type === 'renewal')
        );
      }
    );

    const totalInvoiceCount =
      (sm.stats?.invoice?.eligible.count ?? 0) +
      (sm.stats?.invoice?.excluded.count ?? 0) +
      (seaOffer?.stats?.invoice.awarded.count ?? 0);

    const totalInvoiceAmount =
      (sm.stats?.invoice?.eligible.amount ?? 0) +
      (sm.stats?.invoice?.excluded.amount ?? 0) +
      (seaOffer?.stats?.invoice.awarded.amount ?? 0);

    const seaInstantPayStats = sm.stats?.invoice?.instantPay;

    return {
      id: `${sm.market.uuid},${sm.supplier.uuid}`,
      acceptedDpeWeightedSum: seaOffer?.stats?.invoice.accepted.dpeWeightedSum ?? 0, //takerMarket.accepted.dpeWeightedSum,
      acceptedEarn: seaOffer?.stats?.invoice.accepted.earn ?? 0, //takerMarket.accepted.earn,
      acceptedInvoiceAmount: seaOffer?.stats?.invoice.accepted.amount ?? 0, //takerMarket.accepted.invoiceAmount,
      acceptedInvoiceCount: seaOffer?.stats?.invoice.accepted.count ?? 0, //takerMarket.accepted.invoiceCount,
      clearingDpeWeightedSum: seaOffer?.stats?.invoice.clearing.dpeWeightedSum ?? 0, //clearing dpeWeightedSum is the sum of accepted and awarded dpeWeightedSum,
      clearingEarn: seaOffer?.stats?.invoice.clearing.earn ?? 0, //clearing earn is sum of accepted and awarded earn,
      clearingInvoiceAmount: seaOffer?.stats?.invoice.clearing.amount ?? 0, //clearing amount is sum of accepted and awarded amount,
      clearingInvoiceCount: seaOffer?.stats?.invoice.clearing.count ?? 0, //clearing count is sum of accepted and awarded count,
      clearingDiscountWeightedAvg: seaOffer?.stats?.invoice.clearing.discountWeightedAverage ?? 0, //clearing discountWeightedAverage is sum of accepted and awarded discountWeightedAverage,
      clearingAprWeightedAvg: seaOffer?.stats?.invoice.clearing.aprWeightedAverage ?? 0, //clearing aprWeightedAverage is sum of accepted and awarded aprWeightedAverage,
      clearingDpeWeightedAvg: seaOffer?.stats?.invoice.clearing.dpeWeightedAverage ?? 0, //clearing dpeWeightedAverage is sum of accepted and awarded dpeWeightedAverage,
      acceptancePercentage: calculatePercentage(
        seaOffer?.stats?.invoice.clearing.amount ?? 0,
        (sm.stats?.invoice?.eligible.amount ?? 0) + (seaOffer?.stats?.invoice.awarded.amount ?? 0)
      ), // percent of clearing to what is eligible,
      awardedInvoiceAmount: seaOffer?.stats?.invoice.awarded.amount ?? 0, // Value of invoices that are manually awarded
      awardedInvoiceAmountUsd: (seaOffer?.stats?.invoice.awarded.amount ?? 0) / exchangeRate, // Value of manually awarded invoices converted to USD
      awardedDpeWeightedSum: seaOffer?.stats?.invoice.awarded.dpeWeightedSum ?? 0, //DPE weighted sum of awarded invoices
      awardedInvoiceCount: seaOffer?.stats?.invoice.awarded.count ?? 0, // Number of invoices that have been awarded∂
      currency: sm.market.currency, //takerMarket.market!.currency,
      disableAfterMarketClearsDate: seaOffer?.expirationDate, //takerMarket.offer?.disableAfterMarketClearsDate,
      eligibleDpeWeightedAvg: sm.stats?.invoice?.eligible.dpeWeightedAverage ?? 0, //takerMarket.eligible.dpeWeightedAvg,
      eligibleDpeWeightedSum: sm.stats?.invoice?.eligible.dpeWeightedSum ?? 0, //takerMarket.eligible.dpeWeightedSum,
      eligibleInvoiceAmount: sm.stats?.invoice?.eligible.amount ?? 0, //takerMarket.eligible.invoiceAmount,
      eligibleInvoiceAmountUsd: (sm.stats?.invoice?.eligible.amount ?? 0) / exchangeRate, //takerMarket.eligible.invoiceAmount / exchangeRate,
      eligibleInvoiceCount: sm.stats?.invoice?.eligible.count ?? 0, //takerMarket.eligible.invoiceCount,
      isDiscountMarket: sm.market.isDiscountOnly, //takerMarket.market!.isDiscountOnly,
      instantPay: getInstantPayEnrollment(sm.supplier),
      instantPayStats: seaInstantPayStats
        ? {
            invoiceCount: seaInstantPayStats.count,
            invoiceAmount: seaInstantPayStats.amount,
            invoiceAmountUsd: seaInstantPayStats.amount / exchangeRate,
            dpeWeightedSum: seaInstantPayStats.dpeWeightedSum,
            dpeWeightedAvg: seaInstantPayStats.dpeWeightedAverage,
          }
        : null,
      isInstantPayAllowed: sm.market.isInstantPayAllowed,
      lastClearDate: sm.market.lastClearDate, //takerMarket.market!.lastClearDate,
      legacyMarketId: sm.market.id, //takerMarket.market!.id,
      makerDivisionId: sm.market.makerDivisionId!, //takerMarket.market!.buyerDivision!.id,
      makerDivisionName: sm.market.makerDivisionName!, //takerMarket.market!.buyerDivision!.name,
      makerOrganizationName: supplierMarket.meta.details.makerDisplayName!, //takerMarket.market!.buyerDivision!.organization.name,
      makerOrganizationUuid: sm.market.makerOrganizationUuid, //takerMarket.market!.buyerDivision!.organization.uuid,
      makerOrganizationLogo: supplierMarket.meta.details.makerLogoPngUrl,
      marketFeatures: sm.market.isPreferredEligible ? ['PREFERRED_ELIGIBLE'] : [], //takerMarket.market?.features.map((feature) => feature.feature) ?? [],
      marketMaxApr: sm.market.maxApr, //takerMarket.market!.maxApr,
      marketMaxDiscount: sm.market.maxDiscount, //takerMarket.market!.maxDiscount,
      marketId: sm.market.uuid, //takerMarket.market!.uuid,
      marketIsEnabled: sm.market.enabled, //takerMarket.market!.isEnabled,
      marketIsInactive: sm.supplier.isInactive,
      marketNextClearTime: sm.market.nextClearTime, //takerMarket.market!.nextClearTime,
      marketPayDate: sm.market.payDate, //takerMarket.market!.payDate,
      // If marketGoodFundsDate is not null, convert YYYY-MM-DD date to ISO string
      // This matches the format market.payDate uses and handles off by one day errors in local timezones
      // Example: 2025-09-11 -> 2025-09-11T00:00:00.000Z
      marketGoodFundsDate: sm.market.goodFundsDate ? new Date(sm.market.goodFundsDate).toISOString() : null, //takerMarket.market!.goodFundsDate,
      marketPricingType: convertSEAPricing(supplierMarket.meta.pricingType), //takerMarket.market?.pricing?.type,
      marketIsDSF: sm.market.isDsf,
      staticMarketRate: staticMarketRate?.rate, //takerMarket.market?.pricing && getMarketPricingInformation(takerMarket.market?.pricing),
      marketType: sm.market.type ?? undefined, //takerMarket.market?.marketType,
      marketUuid: sm.market.uuid, //takerMarket.market!.uuid,
      notAcceptedDpeWeightedAvg: seaOffer?.stats?.invoice.notAccepted.dpeWeightedAverage ?? 0, //takerMarket.notAccepted.dpeWeightedAvg,
      notAcceptedDpeWeightedSum: seaOffer?.stats?.invoice.notAccepted.dpeWeightedSum ?? 0, //takerMarket.notAccepted.dpeWeightedSum,
      notAcceptedInvoiceAmount: seaOffer?.stats?.invoice.notAccepted.amount ?? 0, //takerMarket.notAccepted.invoiceAmount,
      notAcceptedInvoiceCount: seaOffer?.stats?.invoice.notAccepted.count ?? 0, //takerMarket.notAccepted.invoiceCount,
      offerConfig: {
        uuid: seaOffer?.uuid,
        isEnabled: seaOffer?.status === 'active', //takerMarket.offer?.isEnabled,
        maxApr: seaOffer?.rateInformation?.rateType === 'APR' ? seaOffer?.rateInformation?.rate : null, //takerMarket.offer?.maxApr,
        maxDiscount: seaOffer?.rateInformation?.rateType === 'DISC' ? seaOffer?.rateInformation?.rate : null, //takerMarket.offer?.maxDiscount,
        divisionId: Number(sm.supplier.id), //takerMarket.supplierDivision!.id,
        isReferenceRateBidding: seaOffer?.rateInformation?.rateType === 'REF', //takerMarket.offer?.isReferenceRateBidding,
        isDiscountBidding: seaOffer?.rateInformation?.rateType === 'DISC', //takerMarket.offer?.isDiscountBidding,
        exclusionSettings: {
          excludeNewInvoices: seaOffer?.excludeNewInvoices,
        }, //takerMarket.offer?.exclusionSettings ? takerMarket.offer.exclusionSettings : {},
        marketId: sm.market.uuid, //takerMarket.market!.uuid,
        marketUuid: sm.market.uuid, //takerMarket.market!.uuid,
        type: convertSEAPricing(supplierMarket.meta.pricingType), //takerMarket.market?.pricing?.type,
        offerType: convertSEAOfferType(sm.offer.offerType), //takerMarket.offer?.offerType,
        frequency: convertSEAOfferFrequency(seaOffer?.frequency),
        isPreferredRenewalRequested:
          seaOffer?.supplierMarkets &&
          seaOffer?.supplierMarkets.length > 0 &&
          seaOffer?.supplierMarkets.every((sm) => sm.actions.isPreferredRenewalRequested),
        isManuallyAwarded: (seaOffer?.stats?.invoice.awarded.count ?? 0) > 0 && seaOffer?.status === 'active',
        isActiveExpressAccept: sm.offer.offerType === 'express-accept' && seaOffer?.status === 'active',
        status: seaOffer?.status ?? null,
      },
      offerOptions: convertSEAOfferOptions(supplierMarket.meta.offerOptions),
      rateInfos: getSEASupplierMarketRateInfos(pricingSuggestion, seaOffer), //getTakerMarketRateInfo(takerMarket),
      takerDivisionId: Number(sm.supplier.id), //takerMarket.supplierDivision!.id,
      takerDivisionUuid: sm.supplier.uuid, //takerMarket.supplierDivision!.uuid,
      takerDivisionName: sm.supplier.name, //takerMarket.supplierDivision!.name,
      takerExcludedInvoiceAmount: sm.stats?.invoice?.excluded.amount ?? 0, //takerMarket.supplierExcluded.invoiceAmount,
      takerExcludedInvoiceCount: sm.stats?.invoice?.excluded.count ?? 0, //takerMarket.supplierExcluded.invoiceCount,
      totalInvoiceAmount,
      totalInvoiceAmountUsd: totalInvoiceAmount / exchangeRate,
      totalInvoiceCount,
      takerMakerId: sm.supplier.buyerId!, //takerMarket.supplierBuyerId,
      lastFetchedAt: Date.now(),
      offerSchedule,
      offerStatsStatus: seaOffer?.stats?.status ?? null,
    } satisfies TakerMarket;
  });
};

// Fetch suppliers associated with the current user
// Used to match user's association date to the division for showing newly added divisions
// Fallback to empty array if request fails as this is not critical to taker markets
const fetchUserSuppliers = async (): Promise<UserSupplier[]> => {
  const response = await apiClient
    .get('api/supplier-experience/user/suppliers')
    .json<UserSupplierResponse>()
    .catch(() => {
      return { data: { suppliers: [] } };
    });

  return response.data.suppliers ?? [];
};

interface SupplierMarketQueryOptions {
  supplierMarkets?: {
    makerOrganizationUuid: string;
    marketUuid: string;
    supplierDivisionUuid: string;
  }[];
}

interface FetchTakerMarketsOptions {
  filter?: SupplierMarketQueryOptions;
  includeInactive?: boolean;
}

export const fetchTakerMarketsWithoutUserSuppliers = async (
  options?: FetchTakerMarketsOptions
): Promise<TakerMarket[]> => {
  const usdExchangeRates: ExchangeRate | undefined = queryClient.getQueryData(['exchange-rates', 'USD']);

  const supplierMarketsFilter =
    (options?.filter && 'supplierMarkets' in options.filter && options.filter.supplierMarkets) || undefined;

  // Fetch taker market data in parallel excluding user suppliers
  const [seaMarkets, seaOffers, seaPricingSuggestions] = await Promise.all([
    apiClient
      .get(`api/supplier-experience/supplier-markets`, {
        searchParams: qs.stringify(
          {
            supplierMarkets: supplierMarketsFilter?.map((sm) => ({
              makerOrganizationUuid: sm.makerOrganizationUuid,
              marketUuid: sm.marketUuid,
              supplierDivisionUuid: sm.supplierDivisionUuid,
            })),
            ...(options?.includeInactive !== undefined && { includeInactive: String(options.includeInactive) }),
          } satisfies SEAMarketParams,
          { arrayFormat: 'indices' }
        ),
      })
      .json<SEAMarketResponse>(),
    apiClient
      .get(`api/supplier-experience/offers`, {
        searchParams: qs.stringify(
          {
            includeStats: 'true',
            supplierMarkets: supplierMarketsFilter?.map((sm) => ({
              makerOrganizationUuid: sm.makerOrganizationUuid,
              marketUuid: sm.marketUuid,
              supplierDivisionUuid: sm.supplierDivisionUuid,
            })),
          } satisfies SEAOfferParams,
          { arrayFormat: 'indices' }
        ),
      })
      .json<SEAOfferResponse>(),
    apiClient
      .post(`api/supplier-experience/pricing-suggestions`, {
        json: {
          rateInformationTypes: ['benchmark', 'static', 'preferred'],
          frequencies: ['recurring'],
          isActionableOffer: false,
          ...(supplierMarketsFilter
            ? {
                // One group per supplier market, to match typical enterprise style grouping
                supplierMarketGroups: supplierMarketsFilter.map((sm) => ({
                  supplierMarkets: [
                    {
                      makerOrganizationUuid: sm.makerOrganizationUuid,
                      marketUuid: sm.marketUuid,
                      supplierDivisionUuid: sm.supplierDivisionUuid,
                    },
                  ],
                })),
              }
            : {}),
        } satisfies SEAPricingSuggestionRequestBody,
      })
      .json<SEAPricingSuggestionResponse>(),
  ]);

  return seaMarkets.data && seaOffers.data
    ? formatSEASupplierMarkets(
        seaMarkets.data.supplierMarketGroups,
        seaOffers.data,
        seaPricingSuggestions.data.supplierMarketGroups,
        usdExchangeRates
      )
    : [];
};

const userSuppliersQueryKey = ['user-suppliers'] as const;

const takerMarketsQueryKey = (includeInactive?: boolean) =>
  includeInactive === true ? (['taker-markets', { includeInactive: true }] as const) : (['taker-markets'] as const);

/**
 * Warms the taker-markets and user-suppliers caches before nav/AppLayout mount so the
 * initial `useTakerMarkets` suspense does not wait behind `ArtifactsGate`.
 * Best-effort first-paint optimization only — failures are swallowed here.
 */
export const prefetchTakerMarketsQueries = (queryClient: QueryClient, includeInactive?: boolean): Promise<void> =>
  Promise.all([
    queryClient.prefetchQuery({
      queryKey: takerMarketsQueryKey(includeInactive),
      queryFn: () => fetchTakerMarketsWithoutUserSuppliers({ includeInactive }),
    }),
    queryClient.prefetchQuery({
      queryKey: userSuppliersQueryKey,
      queryFn: fetchUserSuppliers,
    }),
  ])
    .then(() => undefined)
    .catch(() => {
      // Prefetch failed silently — `useTakerMarkets` will fetch again when a component
      // needs the data and will error there (throwOnError) if the request still fails.
      return undefined;
    });

export const applyInstantPayStats = <T extends TakerMarket>(takerMarket: T, isInstantPayEligible: boolean): T => {
  const stats = takerMarket.instantPayStats;

  if (!isInstantPayEligible || !stats) {
    return takerMarket;
  }

  return {
    ...takerMarket,
    eligibleInvoiceCount: stats.invoiceCount,
    eligibleInvoiceAmount: stats.invoiceAmount,
    eligibleInvoiceAmountUsd: stats.invoiceAmountUsd,
    eligibleDpeWeightedSum: stats.dpeWeightedSum,
    eligibleDpeWeightedAvg: stats.dpeWeightedAvg,
    acceptancePercentage: calculatePercentage(
      takerMarket.clearingInvoiceAmount,
      stats.invoiceAmount + takerMarket.awardedInvoiceAmount
    ),
  };
};

const useTakerMarkets = <TData = TakerMarket[]>(select?: (data: TakerMarket[]) => TData, includeInactive?: boolean) => {
  // Reason: swapped on read, not in the formatter, so the cache stays flag-independent for SSE refreshes
  const { isInstantPayEligible } = useInstantPayEligible();

  return useSuspenseQueries({
    queries: [
      {
        queryKey: takerMarketsQueryKey(includeInactive),
        queryFn: () => fetchTakerMarketsWithoutUserSuppliers({ includeInactive }),
      },
      {
        queryKey: userSuppliersQueryKey,
        queryFn: () => fetchUserSuppliers(),
      },
    ],
    combine: (results) => {
      const takerMarkets = results[0].data;
      const userSuppliers = results[1].data;

      const usdExchangeRates: ExchangeRate | undefined = queryClient.getQueryData(['exchange-rates', 'USD']);

      // match taker markets with user suppliers on supplier uuid to find user added to division date
      const takerMarketsWithUserSuppliers = takerMarkets
        .map((takerMarket) => {
          const userSupplier = userSuppliers.find((supplier) => {
            return supplier.uuid === takerMarket.takerDivisionUuid;
          });

          return {
            ...takerMarket,
            userAddedToDivision: userSupplier?.associationDate,
          };
        })
        .map((takerMarket) => {
          const totalInvoiceCount =
            (takerMarket.eligibleInvoiceCount ?? 0) +
            (takerMarket.takerExcludedInvoiceCount ?? 0) +
            (takerMarket.awardedInvoiceCount ?? 0);

          const totalInvoiceAmount =
            (takerMarket.eligibleInvoiceAmount ?? 0) +
            (takerMarket.takerExcludedInvoiceAmount ?? 0) +
            (takerMarket.awardedInvoiceAmount ?? 0);

          let exchangeRate = usdExchangeRates?.[takerMarket.currency] ?? 1;

          if (exchangeRate === 0) {
            exchangeRate = 1;
          }

          return {
            ...takerMarket,
            totalInvoiceCount,
            totalInvoiceAmount,
            totalInvoiceAmountUsd: totalInvoiceAmount / exchangeRate,
            offerConfig: {
              ...takerMarket.offerConfig,
              isManuallyAwarded:
                (takerMarket.awardedInvoiceCount ?? 0) > 0 && takerMarket.offerConfig.isEnabled === true,
            },
          };
        })
        // after totals, which stay on standard stats
        .map((takerMarket) => applyInstantPayStats(takerMarket, isInstantPayEligible(takerMarket)));

      const selected = select ? select(takerMarketsWithUserSuppliers) : takerMarketsWithUserSuppliers;

      return { data: selected } as TData extends TakerMarket[] ? { data: TakerMarket[] } : { data: TData };
    },
  });
};

export default useTakerMarkets;
